import { createSettingsParams, SettingsParamType } from '@wix/tpa-settings';
import { DataType } from '@wix/editor-platform-ooi-metadata-resolver';

import { withAiDescription } from '../../../../../components-shared/Settings/withAiDescription';
import {
  ADD_TO_CART_BUTTON_PLACEMENT,
  HEADER_PLACEMENT,
  ITEM_IMAGE_PLACEMENT,
  ITEM_IMAGE_RATIO,
  ITEM_IMAGE_RESIZE,
  ITEM_LAYOUT_TYPE,
} from '../../../constants';

export type LayoutSettingsParams = {
  itemLayoutType: SettingsParamType.Number;
  itemImagePlacement: SettingsParamType.Number;
  itemImageRatio: SettingsParamType.Number;
  itemImageResize: SettingsParamType.Number;
  headerImagePlacement: SettingsParamType.Number;
  addToCartButtonPlacement: SettingsParamType.Number;
};

// Declared inline (not via `layoutSettingsParams.itemLayoutType`) because
// `itemImageRatio` needs it inside the same `createSettingsParams` call, and
// referencing the exported const in its own initializer makes it implicitly
// `any` for TypeScript. `key` must stay in sync with the object key below.
const itemLayoutTypeParam = {
  key: 'itemLayoutType',
  type: SettingsParamType.Number,
  getDefaultValue: () => ITEM_LAYOUT_TYPE.STRIP,
};

/**
 * `metadata` exposes these params in the widget's editor manifest
 * (`editorElement.data`), which is what Aria reads and edits in Studio 2.
 * Params without `metadata` are dropped from the manifest. `withAiDescription`
 * gives each param one description of its stored numbers for both Aria
 * versions. Aria writes each param on its own, so the layout description tells
 * it which related params the Settings panel changes together with the layout
 * (see MenuLayout).
 */
export const layoutSettingsParams = createSettingsParams<LayoutSettingsParams>({
  itemLayoutType: {
    type: SettingsParamType.Number,
    getDefaultValue: () => ITEM_LAYOUT_TYPE.STRIP,
    metadata: ({ t }) => ({
      type: DataType.number,
      defaultValue: {
        type: 'static',
        value: ITEM_LAYOUT_TYPE.STRIP,
      },
      ...withAiDescription({
        displayName: t('app.settings.layout.menu.choose-layout'),
        description:
          'Menu item layout: 0 = Strip (list, rows, image beside text), 1 = Card (grid, tiles, image on top). ' +
          'No column count, masonry or carousel. On a switch also set itemImageRatio (Card 2, Strip 0), ' +
          'addToCartButtonPlacement (Card 0, Strip 1), style quickAddHorizontalAlignment (Card 0, Strip 2).',
      }),
    }),
  },
  itemImagePlacement: {
    type: SettingsParamType.Number,
    getDefaultValue: () => ITEM_IMAGE_PLACEMENT.RIGHT,
    metadata: ({ t }) => ({
      type: DataType.number,
      defaultValue: { type: 'static', value: ITEM_IMAGE_PLACEMENT.RIGHT },
      ...withAiDescription({
        displayName: t('app.settings.layout.menu.image-placement'),
        description:
          'Strip layout only: side of the menu item image, 0 = right (default), 1 = left. ' +
          'Card layout always shows the image on top.',
      }),
    }),
  },
  itemImageRatio: {
    type: SettingsParamType.Number,
    // No stored value means the site predates the Image ratio control, so fall
    // back to the shape it already rendered: strip 1:1, card 16:9. This is what
    // keeps existing card sites off 1:1 — the viewer reads these settings
    // ungated, so a flat SQUARE default would reflow every card site on deploy.
    getDefaultValue: ({ getSettingParamValue }) =>
      getSettingParamValue(itemLayoutTypeParam) === ITEM_LAYOUT_TYPE.CARD
        ? ITEM_IMAGE_RATIO.WIDE
        : ITEM_IMAGE_RATIO.SQUARE,
    metadata: ({ t }) => ({
      type: DataType.number,
      defaultValue: { type: 'static', value: ITEM_IMAGE_RATIO.SQUARE },
      ...withAiDescription({
        displayName: t('app.settings.layout.menu.image-ratio'),
        description:
          'Menu item image shape, per layout. Card: 0 = square 1:1, 1 = landscape 4:3, 2 = wide 16:9 ' +
          '(default). Strip: 0 = square 1:1 (default), 1 = portrait 3:4, 2 = tall 9:16. A Card layout never ' +
          'changed here shows 16:9 even when the value reads 0.',
      }),
    }),
  },
  itemImageResize: {
    type: SettingsParamType.Number,
    getDefaultValue: () => ITEM_IMAGE_RESIZE.CROP,
    metadata: ({ t }) => ({
      type: DataType.number,
      defaultValue: { type: 'static', value: ITEM_IMAGE_RESIZE.CROP },
      ...withAiDescription({
        displayName: t('app.settings.layout.menu.resize-options'),
        description:
          'How the menu item image fills its frame: 0 = crop to fill the frame (default), ' +
          '1 = fit the whole image inside the frame.',
      }),
    }),
  },
  headerImagePlacement: {
    type: SettingsParamType.Number,
    getDefaultValue: () => HEADER_PLACEMENT.BELOW,
    metadata: ({ t }) => ({
      type: DataType.number,
      defaultValue: { type: 'static', value: HEADER_PLACEMENT.BELOW },
      ...withAiDescription({
        displayName: t('app.settings.layout.header.image'),
        description:
          'Header cover image position: 0 = title and description below the image (default), ' +
          '1 = title and description on top of the image, used as a background.',
      }),
    }),
  },
  addToCartButtonPlacement: {
    type: SettingsParamType.Number,
    // Desktop and mobile intentionally resolve to different defaults for the
    // same layout (see getDefaultValue below), so mobile must never fall back
    // to desktop's stored/default value — it always computes its own.
    inheritDesktop: false,
    // Strip has room for the button over the image only on desktop; Card and
    // mobile always keep it in the item info (mirrors handleStripLayoutClick's
    // interactive default in Menu/index.tsx).
    getDefaultValue: ({ isMobile, getSettingParamValue }) =>
      !isMobile &&
      getSettingParamValue(itemLayoutTypeParam) === ITEM_LAYOUT_TYPE.STRIP
        ? ADD_TO_CART_BUTTON_PLACEMENT.ON_IMAGE
        : ADD_TO_CART_BUTTON_PLACEMENT.ITEM_INFO,
    metadata: ({ t }) => ({
      type: DataType.number,
      defaultValue: {
        type: 'static',
        value: ADD_TO_CART_BUTTON_PLACEMENT.ON_IMAGE,
      },
      mobileDefaultValue: {
        type: 'static',
        value: ADD_TO_CART_BUTTON_PLACEMENT.ITEM_INFO,
      },
      ...withAiDescription({
        displayName: t(
          'app.settings.layout.menu.add-to-cart-button.button-placement',
        ),
        description:
          'Add to cart button position: 0 = in the item info, 1 = over the image. Strip on mobile always uses ' +
          'item info; a Card layout never changed here shows item info even when it reads 1. When changing it, ' +
          'also set styles quickAddButtonWidth = 1 and quickAddHorizontalAlignment = 0 (item info) or 2 (image).',
      }),
    }),
  },
});
